import { useCallback, useState } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { LanguageTranslationsSavePayload, Props } from './types';

const getUrl = (projectKey: ProjectKey | undefined): string =>
	`/rest/servicedesk/lingo-integration/1/manage-translations/${projectKey}`;

export const useTranslationsSaveDataService = ({ projectKey, onSuccess, onFailure }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [translationsSaved, setTranslationsSaved] = useState({
		isSuccess: false,
	});

	const postRequest = useCallback(
		async (payload: LanguageTranslationsSavePayload): Promise<void> => {
			try {
				const url = getUrl(projectKey);

				const options = {
					body: JSON.stringify(payload),
				};

				await performPostRequest(url, options);
				setTranslationsSaved({ isSuccess: true });
				fireTrackAnalytics(createAnalyticsEvent({}), 'translation saved');
				if (onSuccess) {
					onSuccess();
				}
			} catch (err: unknown) {
				setTranslationsSaved({ isSuccess: false });
				fireErrorAnalytics({
					event: createAnalyticsEvent({
						action: 'saveTranslations',
						actionSubject: 'failed',
					}),
					error: err instanceof Error ? err : undefined,
					meta: {
						id: 'useTranslationsSaveDataService',
						packageName: 'jiraLanguageSettings',
						teamName: 'jsm-nimbus',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				if (onFailure) {
					onFailure(err);
				}

				// Rethrow the error after firing error analytics
				throw err;
			}
		},
		[createAnalyticsEvent, onFailure, onSuccess, projectKey],
	);

	const { loading, error, fetch: saveLanguageTranslationsData } = useService(postRequest);

	return [
		{ error, loading, translationsSaved },
		{
			saveLanguageTranslationsData,
		},
	];
};
